import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { useAuth } from "@/contexts/AuthContext";
import { AUTH } from "@/constants/testIds";
import { toast } from "sonner";
import { Loader2, Users, GraduationCap } from "lucide-react";

export default function Register() {
  const { register, extractError } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const initialRole = params.get("role") === "sitter" ? "sitter" : "family";
  const initialEmail = params.get("email") || "";
  const initialName = params.get("name") || "";
  const inviteToken = params.get("invite") || "";

  const [role, setRole] = useState(initialRole);
  const [name, setName] = useState(initialName);
  const [email, setEmail] = useState(initialEmail);
  const [password, setPassword] = useState("");
  const [phone, setPhone] = useState("");
  const [smsOptIn, setSmsOptIn] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      const u = await register(email, password, name, role, phone.trim(), !!(smsOptIn && phone.trim()), inviteToken);
      toast.success(`Welcome, ${u.name}!`);
      // Route new families straight into their profile so they don't land on an empty dashboard
      // with nothing to do. Sitters go to their dashboard where the verification checklist lives.
      navigate(role === "family" ? "/family/profile?welcome=1" : "/sitter");
    } catch (err) {
      const m = extractError(err); setError(m); toast.error(m);
    } finally { setLoading(false); }
  };

  const RoleTile = ({ value, icon, label, desc, testId }) => (
    <button
      type="button" onClick={() => setRole(value)} data-testid={testId}
      className={`text-left rounded-xl border p-5 transition-colors duration-200 ${
        role === value
          ? "border-sage-600 bg-sage-50"
          : "border-stone2-200 bg-white hover:border-sage-300"
      }`}
    >
      <div className={`w-9 h-9 rounded-full flex items-center justify-center mb-3 ${
        role === value ? "bg-sage-600 text-white" : "bg-stone2-100 text-sage-700"
      }`}>{icon}</div>
      <div className="font-heading font-semibold text-sage-900">{label}</div>
      <div className="text-xs text-stone2-600 mt-1 leading-relaxed">{desc}</div>
    </button>
  );

  return (
    <div className="min-h-screen bg-stone2-50">
      <Navbar />
      <div className="max-w-lg mx-auto px-4 py-14">
        <span className="section-eyebrow">Create your account</span>
        <h1 className="mt-2 text-3xl font-heading font-bold text-sage-900">Join AutismBabysitters</h1>

        <div className="mt-6 grid grid-cols-2 gap-3">
          <RoleTile value="family" icon={<Users className="w-4 h-4" />}
            label="I'm a family" desc="Find specialized sitters for my child"
            testId={AUTH.registerRoleFamily} />
          <RoleTile value="sitter" icon={<GraduationCap className="w-4 h-4" />}
            label="I'm a sitter" desc="Offer specialized care & get booked"
            testId={AUTH.registerRoleSitter} />
        </div>

        <form onSubmit={submit} className="mt-6 paper-card p-6 space-y-5" data-testid="register-form">
          <div>
            <label className="field-label">Full name</label>
            <input required data-testid={AUTH.registerName} value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full rounded-lg border border-stone2-200 bg-white px-4 py-2.5 text-sm focus:ring-2 focus:ring-sage-500 focus:border-sage-500 outline-none" />
          </div>
          <div>
            <label className="field-label">Email</label>
            <input required type="email" data-testid={AUTH.registerEmail} value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full rounded-lg border border-stone2-200 bg-white px-4 py-2.5 text-sm focus:ring-2 focus:ring-sage-500 focus:border-sage-500 outline-none" />
          </div>
          <div>
            <label className="field-label">Password (min 6)</label>
            <input required type="password" minLength={6} data-testid={AUTH.registerPassword} value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full rounded-lg border border-stone2-200 bg-white px-4 py-2.5 text-sm focus:ring-2 focus:ring-sage-500 focus:border-sage-500 outline-none" />
          </div>

          {/* Optional phone + SMS opt-in */}
          <div>
            <label className="field-label" htmlFor="reg-phone">
              Mobile phone <span className="text-stone2-400 font-normal">(optional — for text alerts)</span>
            </label>
            <input id="reg-phone" type="tel" data-testid="register-phone-input" value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="(770) 555-1234"
              autoComplete="tel"
              className="w-full rounded-lg border border-stone2-200 bg-white px-4 py-2.5 text-sm focus:ring-2 focus:ring-sage-500 focus:border-sage-500 outline-none" />
          </div>
          <label className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer ${
            phone.trim() ? "border-stone2-200 bg-white" : "border-stone2-100 bg-stone2-50 opacity-70"
          }`} data-testid="register-sms-optin-label">
            <input type="checkbox" checked={smsOptIn}
              onChange={(e) => setSmsOptIn(e.target.checked)}
              disabled={!phone.trim()}
              data-testid="register-sms-optin"
              className="mt-0.5 rounded text-sage-600" />
            <span className="text-xs text-stone2-700 leading-relaxed">
              <strong className="text-sage-900 text-sm block mb-1">
                Text me transactional alerts
              </strong>
              I agree to receive SMS from AutismBabysitters.com about new messages,
              booking requests, booking status, and account alerts (e.g. password reset).
              Message frequency varies. Message &amp; data rates may apply.
              Reply <strong>STOP</strong> to unsubscribe. Reply <strong>HELP</strong> for help.
            </span>
          </label>
          {error && <div className="text-sm text-danger" data-testid="register-error">{error}</div>}
          <button type="submit" disabled={loading} data-testid={AUTH.registerSubmit} className="btn-primary w-full justify-center">
            {loading && <Loader2 className="w-4 h-4 animate-spin" />} Create account
          </button>
        </form>

        <p className="mt-6 text-sm text-stone2-600">
          Already registered? <Link to="/login" className="text-sage-800 font-semibold hover:underline">Log in</Link>
        </p>
      </div>
    </div>
  );
}
